import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthState } from "react-firebase-hooks/auth";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { auth, db, storage } from "../../firebase/config";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { useToast } from "../../components/ui/use-toast";
import { useAutoDeployment } from "../../hooks/useAutoDeployment";
import EditorWrapper from "../../components/EditorWrapper";
import {
  Loader2,
  PenLine,
  User,
  FileText,
  Send,
  ImageIcon,
  X,
  Calendar,
} from "lucide-react";
import { motion } from "framer-motion";

const CreateBlog = () => {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { toast } = useToast();
  const { triggerDeployment } = useAutoDeployment();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    author: user?.displayName || "",
    contentHtml: "",
    thumbnailUrl: "",
    publishDate: new Date().toISOString().split("T")[0], // Default to today
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith("image/")) {
      toast({
        variant: "destructive",
        title: "Invalid File",
        description: "Please upload an image file",
      });
      return;
    }

    // Check file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File Too Large",
        description: "Image size should be less than 5MB",
      });
      return;
    }

    setUploadingImage(true);

    try {
      // Create a unique file name
      const fileName = `${Date.now()}-${file.name}`;
      const storageRef = ref(storage, `blogsWeb/${fileName}`);

      // Upload the file
      await uploadBytes(storageRef, file);

      // Get the download URL
      const downloadUrl = await getDownloadURL(storageRef);

      // Set the preview and save the URL
      setThumbnailPreview(URL.createObjectURL(file));
      setFormData((prev) => ({ ...prev, thumbnailUrl: downloadUrl }));

      toast({
        title: "Success",
        description: "Thumbnail uploaded successfully",
      });
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Upload Failed",
        description: "Failed to upload thumbnail. Please try again.",
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const removeThumbnail = () => {
    setThumbnailPreview(null);
    setFormData((prev) => ({ ...prev, thumbnailUrl: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (
      !formData.title ||
      !formData.description ||
      !formData.contentHtml ||
      !formData.thumbnailUrl ||
      !formData.publishDate
    ) {
      toast({
        variant: "destructive",
        title: "Missing Fields",
        description:
          "Please fill in all required fields including thumbnail and publish date",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert the publish date to a Firestore Timestamp
      const publishDateTime = new Date(formData.publishDate);
      publishDateTime.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues

      const docRef = await addDoc(collection(db, "BlogsWeb"), {
        ...formData,
        createdAt: publishDateTime,
        publishDate: publishDateTime,
      });

      toast({
        title: "Success",
        description: "Blog post created successfully! 🎉",
      });

      // Auto-trigger deployment after successful blog creation
      try {
        console.log("🚀 Triggering auto-deployment for new blog...");
        const deployResult = await triggerDeployment({
          id: docRef.id,
          title: formData.title,
          action: "created",
        });

        if (deployResult.success) {
          toast({
            title: "Deployment Triggered! 🚀",
            description: "Your blog post will be live in 2-3 minutes.",
          });
        } else {
          toast({
            title: "Blog Created ✅",
            description:
              "Blog saved successfully, but auto-deployment failed. You can trigger it manually.",
            variant: "default",
          });
        }
      } catch (deployError) {
        console.error("Auto-deployment failed:", deployError);
        toast({
          title: "Blog Created ✅",
          description:
            "Blog saved successfully! Auto-deployment encountered an issue, but your blog is saved.",
          variant: "default",
        });
      }

      navigate("/admin/blogs");
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create blog post. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditorChange = useCallback((html: string) => {
    setFormData((prev) => ({ ...prev, contentHtml: html }));
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="max-w-5xl mx-auto p-6 space-y-8"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-forest-green/10 flex items-center justify-center">
            <PenLine className="h-5 w-5 text-forest-green" />
          </div>
          <h1 className="font-heading text-2xl text-forest-green">
            Create New Blog
          </h1>
        </div>
        <Button
          variant="outline"
          className="border-forest-green text-forest-green hover:bg-forest-green/5 transition-colors duration-300"
          onClick={() => navigate("/admin/blogs")}
        >
          Cancel
        </Button>
      </div>

      <motion.form
        onSubmit={handleSubmit}
        className="space-y-6 bg-white rounded-xl p-6 shadow-sm border border-misty-sage/20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
      >
        <div className="space-y-6">
          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            <label
              htmlFor="thumbnail"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <ImageIcon className="h-4 w-4" />
              Blog Thumbnail
            </label>
            <div className="relative">
              <input
                type="file"
                id="thumbnail"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
              />
              {thumbnailPreview ? (
                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-misty-sage/20">
                  <img
                    src={thumbnailPreview}
                    alt="Thumbnail preview"
                    className="w-full h-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={removeThumbnail}
                    className="absolute top-2 right-2 p-1 rounded-full bg-white/80 hover:bg-white transition-colors"
                  >
                    <X className="h-4 w-4 text-forest-green" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full aspect-video border-dashed border-2 border-misty-sage hover:border-forest-green transition-colors duration-300"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="mr-2 h-4 w-4" />
                      Upload Thumbnail
                    </>
                  )}
                </Button>
              )}
            </div>
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.3 }}
          >
            <label
              htmlFor="title"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <FileText className="h-4 w-4" />
              Blog Title
            </label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, title: e.target.value }))
              }
              placeholder="Enter an engaging title for your blog"
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 placeholder:text-forest-green/30 text-forest-green"
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.4 }}
          >
            <label
              htmlFor="description"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <FileText className="h-4 w-4" />
              Short Description
            </label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              placeholder="Write a brief and compelling description (2-3 lines)"
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 resize-none placeholder:text-forest-green/30 text-forest-green"
              rows={3}
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
          >
            <label
              htmlFor="author"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <User className="h-4 w-4" />
              Author Name
            </label>
            <Input
              id="author"
              value={formData.author}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, author: e.target.value }))
              }
              placeholder="Enter the author's name"
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 placeholder:text-forest-green/30 text-forest-green"
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.55 }}
          >
            <label
              htmlFor="publishDate"
              className="flex items-center gap-2 text-sm font-medium text-forest-green"
            >
              <Calendar className="h-4 w-4" />
              Publish Date
            </label>
            <Input
              id="publishDate"
              type="date"
              value={formData.publishDate}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  publishDate: e.target.value,
                }))
              }
              className="border-misty-sage focus:border-forest-green transition-colors duration-300 text-forest-green"
              required
            />
          </motion.div>

          <motion.div
            className="space-y-2"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.65 }}
          >
            <label className="flex items-center gap-2 text-sm font-medium text-forest-green">
              <PenLine className="h-4 w-4" />
              Content
            </label>
            <div className="rounded-lg border border-misty-sage focus-within:border-forest-green transition-colors duration-300">
              <EditorWrapper onChange={handleEditorChange} height="500px" />
            </div>
          </motion.div>
        </div>

        <motion.div
          className="flex justify-end pt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.75 }}
        >
          <Button
            type="submit"
            className="bg-forest-green hover:bg-forest-green/90 transition-colors duration-300 min-w-[150px] h-11"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Send className="mr-2 h-4 w-4" />
                Create Blog
              </>
            )}
          </Button>
        </motion.div>
      </motion.form>
    </motion.div>
  );
};

export default CreateBlog;
